import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Copy, QrCode, X } from 'lucide-react';
import { api } from '@/services/api';
import { useTheme } from '@/contexts/ThemeContext';

function useBodyScrollLock(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [enabled]);
}

export default function BenefitQRModal({ benefit, language = 'es', onClose }) {
  const { theme } = useTheme();
  const es = language === 'es';
  const [copied, setCopied] = useState(false);
  const [redeem, setRedeem] = useState(null);
  const [redeemError, setRedeemError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useBodyScrollLock(true);

  const appOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://www.wannacolive.app';
  const guestLink = `${appOrigin}/?tab=benefits&id=${benefit.id}`;

  useEffect(() => {
    if (!redeem) return undefined;
    setSecondsLeft(redeem.expires_in || 300);
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [redeem]);

  const generateRedeem = async () => {
    setGenerating(true);
    setRedeemError('');
    try {
      const data = await api.benefitRedeemToken(benefit.id);
      setRedeem(data);
    } catch (e) {
      setRedeemError(e?.response?.data?.detail || (es ? 'No se pudo generar el QR de canje.' : 'Could not generate the redeem QR.'));
    } finally {
      setGenerating(false);
    }
  };

  const copyText = async (text) => {
    let didCopy = false;
    if (navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(text); didCopy = true; } catch { didCopy = false; }
    }
    if (!didCopy && document.execCommand) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try { didCopy = document.execCommand('copy'); } catch { didCopy = false; }
      document.body.removeChild(textarea);
    }
    if (didCopy) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const expired = redeem && secondsLeft <= 0;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/60 p-3 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="benefit-qr-title"
      data-testid="benefit-qr-modal"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={`flex max-h-[min(80dvh,42rem)] w-full max-w-md min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface ${theme === 'dark' ? 'shadow-none' : 'shadow-2xl'}`}>
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground">
              <QrCode className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 id="benefit-qr-title" className="font-heading text-lg font-semibold text-foreground">{benefit.title}</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">{benefit.business_name}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 overflow-y-auto px-5 py-4">
          <section>
            <h3 className="text-sm font-semibold text-foreground">{es ? 'QR para huéspedes' : 'Guest QR'}</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {es ? 'El huésped lo escanea para abrir y canjear este beneficio en la app.' : 'Guests scan it to open and redeem this benefit in the app.'}
            </p>
            <div className="mt-3 flex flex-col items-center gap-3">
              <div className="rounded-xl border border-border bg-white p-3">
                <QRCodeSVG value={guestLink} size={180} level="M" data-testid="benefit-static-qr" />
              </div>
              <button
                type="button"
                onClick={() => copyText(guestLink)}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                data-testid="copy-benefit-link"
              >
                {copied ? <Check className="h-4 w-4 text-accent-mint" /> : <Copy className="h-4 w-4" />}
                {copied ? (es ? 'Copiado' : 'Copied') : (es ? 'Copiar link' : 'Copy link')}
              </button>
            </div>
          </section>

          <section className="mt-5 border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-foreground">{es ? 'QR de canje' : 'Redeem QR'}</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {es ? 'Vista previa del código que muestra el huésped al comercio (válido por 5 minutos).' : 'Preview of the code the guest shows the merchant (valid for 5 minutes).'}
            </p>

            {redeemError && <p className="mt-2 text-xs text-destructive">{redeemError}</p>}

            {!redeem ? (
              <button
                type="button"
                onClick={generateRedeem}
                disabled={generating}
                className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-foreground px-4 font-heading text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                data-testid="generate-redeem-qr"
              >
                <QrCode className="h-4 w-4" />
                {generating ? (es ? 'Generando…' : 'Generating…') : (es ? 'Generar QR de canje' : 'Generate redeem QR')}
              </button>
            ) : (
              <div className="mt-3 flex flex-col items-center gap-2">
                <div className={`rounded-xl border border-border bg-white p-3 ${expired ? 'opacity-40' : ''}`}>
                  <QRCodeSVG value={redeem.verify_url} size={180} level="M" data-testid="benefit-redeem-qr" />
                </div>
                <p className={`text-xs font-medium ${expired ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="benefit-redeem-timer">
                  {expired
                    ? (es ? 'Código expirado' : 'Code expired')
                    : `${es ? 'Expira en' : 'Expires in'} ${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`}
                </p>
                <button
                  type="button"
                  onClick={generateRedeem}
                  disabled={generating}
                  className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {es ? 'Regenerar' : 'Regenerate'}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
